import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Card,
    Typography,
    Tabs,
    Badge,
    message,
    Select,
    Space,
} from "antd";
import {
    BarChartOutlined,
    DashboardOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import MoodAnalytics from "./MoodAnalytics";
import dayjs from "dayjs";
import CommonSelect from "../../../core/common/commonSelect";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

export interface MoodResponse {
    id: string;
    userId: string;
    userName?: string;
    department: string;
    mood: string;
    score: number;
    timestamp: Date;
    note?: string;
    questionId: string;
}

export interface MoodQuestion {
    id: string;
    question_text?: string;
    question_type?: string;
    text: string;
    type: 'single' | 'multiple' | 'scale';
    options: MoodOption[];
    category?: string;
    active: boolean;
    status?: any;
}

export interface MoodOption {
    id: string;
    text: string;
    emoji: string;
    color: string;
    score: number;
}

const API_DEBOUNCE_TIMEOUT = 300; // ms

// Add this custom hook for API caching
const useApiCache = () => {
    const cache = useRef<Map<string, { data: any; timestamp: number }>>(new Map());
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    const get = (key: string) => {
        const cached = cache.current.get(key);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
            return cached.data;
        }
        return null;
    };

    const set = (key: string, data: any) => {
        cache.current.set(key, { data, timestamp: Date.now() });
    };

    const clear = (key?: string) => {
        if (key) {
            cache.current.delete(key);
        } else {
            cache.current.clear();
        }
    };

    return { get, set, clear };
};

const MoodMeterDashboard: React.FC = () => {
    const signature = localStorage.getItem("signature");
    const user = useSelector((state: any) => {
        return state.user.user;
    });

    // Cache instance
    const apiCache = useApiCache();

    // Track API calls to prevent duplicates
    const apiCallInProgress = useRef<Set<string>>(new Set());

    // Add a ref to track if initial data has been loaded
    const initialLoadDone = useRef(false);

    const currentUserId = user?.userDetails?.id;
    const userName = user?.userDetails?.fname;
    const userRole = user?.userDetails?.role_char;
    const departmentId = user?.userDetails?.department;

    // State
    const [activeTab, setActiveTab] = useState<string>("analytics");
    const [loading, setLoading] = useState<boolean>(false);
    const [userSubmittedToday, setUserSubmittedToday] = useState<boolean>(false);

    // Shared data state
    const [questions, setQuestions] = useState<MoodQuestion[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);

    // Company selection state
    const [selectedCompany, setSelectedCompany] = useState<{
        id: string | null;
        type: string | null;
        name: string;
    }>({
        id: user.company_id,
        type: localStorage.account_type,
        name: "",
    });

    // Date range for analytics
    const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs(),
        dayjs(),
    ]);

    // Departments filter
    const [selectedDepartment, setSelectedDepartment] = useState<string>("all");


    const makeApiCall = useCallback(async (
        endpoint: string,
        payload: any,
        cacheKey?: string,
        forceRefresh = false
    ) => {
        const callId = `${endpoint}-${JSON.stringify(payload)}`;

        const abortController = new AbortController();

        abortControllers.current.set(callId, abortController);

        if (apiCallInProgress.current.has(callId)) {
            return null;
        }

        try {
            apiCallInProgress.current.add(callId);

            const response = await api.post(endpoint, payload, {
                signal: abortController.signal
            });

            // Remove from tracking
            abortControllers.current.delete(callId);

            if (cacheKey && response.data) {
                apiCache.set(cacheKey, response.data);
            }

            return response.data;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.log('Request aborted:', callId);
                return null;
            }
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        } finally {
            apiCallInProgress.current.delete(callId);
            abortControllers.current.delete(callId);
        }
    }, [apiCache]);

    const abortControllers = useRef<Map<string, AbortController>>(new Map());

    // 1. Fetch Companies API - Called only once
    const fetchCompanies = useCallback(async (force = false) => {
        const cacheKey = 'companies';

        // Don't refetch if we already have companies and not forcing refresh
        if (!force && companies.length > 0 && !selectedCompany.id) {
            return;
        }

        try {
            const data = await makeApiCall(
                "userService/getAllCompanyList",
                { signature },
                cacheKey,
                force
            );

            if (data?.msg === "Data Found") {
                const companiesData = data.data?.data || [];
                setCompanies(companiesData);

                // Format options for CommonSelect
                const formattedOptions = companiesData.map((company: any) => ({
                    value: `${company.id}-${company.type}`,
                    label: `${company.name} - ${company.type}`,
                    companyName: company.name,
                    companyId: company.id,
                    companyType: company.type,
                }));
                setCompanyOptions(formattedOptions);

                // Auto-select logic - only if no company is selected
                if (!selectedCompany.id && companiesData.length > 0) {
                    const firstCompany = companiesData[0];
                    await handleCompanyChange({
                        value: `${firstCompany.id}-${firstCompany.type}`,
                        label: `${firstCompany.name} - ${firstCompany.type}`,
                        companyName: firstCompany.name,
                        companyId: firstCompany.id,
                        companyType: firstCompany.type,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            message.error("Failed to load companies");
        }
    }, [signature, makeApiCall, selectedCompany.id]);

    // 2. Fetch Questions API - Centralized
    const fetchQuestions = useCallback(async (companyId: string | null, companyType: string | null, force = false) => {
        if (!companyId || !companyType) return [];

        const cacheKey = `questions-${companyId}-${companyType}`;

        try {
            const data = await makeApiCall(
                "userService/getAllQuestions",
                {
                    signature,
                    company_id: companyId,
                    company_type: companyType,
                },
                cacheKey,
                force
            );

            if (data?.message?.[0]?.status === true) {
                const rawQuestions = data?.message?.[0]?.data || [];

                const normalizedQuestions = rawQuestions.map((q: any) => ({
                    ...q,
                    id: q.id?.toString() || q._id?.toString() || Date.now().toString(),
                    text: q.question_text || q.text || '',
                    type: q.question_type || q.type || 'single',
                    options: Array.isArray(q.options) ? q.options : [],
                    category: q.category || '',
                    status: q.status !== undefined ? q.status : 1,
                    active: q.status === 1,
                }));

                setQuestions(normalizedQuestions);
                return normalizedQuestions;
            } else {
                message.error("Failed to load questions");
                return [];
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            message.error("Error loading questions");
            return [];
        }
    }, [signature, makeApiCall]);

    // 3. Fetch Departments API - Centralized
    const fetchDepartments = useCallback(async (force = false) => {
        const cacheKey = 'departments';

        // Return cached departments if available and not forcing refresh
        if (!force && departments.length > 0) {
            return departments;
        }

        try {
            const data = await makeApiCall(
                "userService/getDepartment",
                { signature },
                cacheKey,
                force
            );

            if (data?.msg === "Data Found") {
                const deptData = data.data?.data || [];

                // Format for dropdown
                const formattedDepartments = [
                    { id: "all", name: "All Departments", key: "all", Department: "All Departments" },
                    ...deptData.map((dept: any) => ({
                        id: dept.key,
                        name: dept.Department,
                        key: dept.key,
                        Department: dept.Department,
                        NoOfEmployees: dept.NoOfEmployees
                    }))
                ];

                setDepartments(formattedDepartments);
                return formattedDepartments;
            }
            return [];
        } catch (error) {
            console.error("Error fetching departments:", error);
            return [];
        }
    }, [signature, makeApiCall, departments.length]);


    // 5. Handle company change
    const handleCompanyChange = async (selectedOption: any) => {
        if (!selectedOption) return;

        const [companyId, companyType] = selectedOption.value.split('-');
        const companyName = selectedOption.companyName || selectedOption.label.split('-')[0]?.trim();

        const newCompany = {
            id: companyId,
            type: companyType,
            name: companyName,
        };

        setSelectedCompany(newCompany);
        setLoading(true);

        // Save to localStorage for persistence
        localStorage.setItem("selectedCompanyId", companyId);
        localStorage.setItem("selectedCompanyType", companyType);
        localStorage.setItem("selectedCompanyName", companyName);

        try {
            await fetchQuestions(companyId, companyType);

            fetchDepartments().catch(console.error);

            message.success(`Switched to ${companyName}`);
        } catch (error) {
            console.error("Error loading company data:", error);
            message.error("Failed to load company data");
        } finally {
            setLoading(false);
        }
    };

    // 6. Handle mood submission
    const handleMoodSubmit = async (response: {
        questionId: string;
        selectedOption: MoodOption;
        note?: string;
    }) => {
        if (!selectedCompany.id) {
            message.error("Please select a company first");
            return;
        }

        try {
            const payload = {
                signature,
                question_id: response.questionId,
                selected_option_id: response.selectedOption.id,
                mood_text: response.selectedOption.text,
                score: response.selectedOption.score,
                note: response.note || "",
                department_id: departmentId,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                is_anonymous: true,
            };

            const apiResponse = await api.post("userService/submitMoodResponse", payload);

            if (apiResponse.data?.message?.[0]?.status === true) {
                setUserSubmittedToday(true);
                message.success({
                    content: "Mood submitted successfully!",
                    icon: "😊",
                    duration: 2,
                });

                if (activeTab === "questionnaire") {
                    setActiveTab("analytics");
                }
            } else {
                message.error(apiResponse.data?.msg || "Failed to submit mood");
            }
        } catch (error: any) {
            console.error("Error submitting mood:", error);

            if (error?.data?.message[0]?.msg?.includes("already submitted")) {
                message.warning("You've already submitted your mood for today");
                setUserSubmittedToday(true);
            } else {
                message.error("Error submitting mood. Please try again.");
            }
        }
    };

    // 7. Handle date range change
    const handleDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs]) => {
        setSelectedDateRange(dates);
    };

    // 8. Handle department filter change
    const handleDepartmentChange = (deptId: string) => {
        setSelectedDepartment(deptId);
    };

    // 9. Refresh all data
    const handleRefresh = useCallback(async () => {
        setLoading(true);
        try {
            // Only refresh data for current company if selected
            if (selectedCompany.id && selectedCompany.type) {
                await Promise.all([
                    fetchQuestions(selectedCompany.id, selectedCompany.type, true), // Force refresh
                    fetchDepartments(true), // Force refresh
                ]);
            } else {
                // If no company selected, just refresh companies
                await fetchCompanies(true);
            }
            message.info("Data refreshed");
        } catch (error) {
            console.error("Error refreshing data:", error);
            message.error("Failed to refresh data");
        } finally {
            setLoading(false);
        }
    }, [selectedCompany.id, selectedCompany.type, fetchCompanies, fetchQuestions, fetchDepartments,]);

    useEffect(() => {
        return () => {
            apiCallInProgress.current.clear();
        };
    }, []);

    // Initialize
    useEffect(() => {
        if (signature && !initialLoadDone.current) {
            const initData = async () => {
                setLoading(true);
                initialLoadDone.current = true;

                try {
                    // 1. Load companies first
                    await fetchCompanies();

                    // 2. If we have a stored company, load its data
                    const storedCompanyId = localStorage.getItem("selectedCompanyId");
                    const storedCompanyType = localStorage.getItem("selectedCompanyType");

                    if (storedCompanyId && storedCompanyType) {
                        // Only load questions and check submission for stored company
                        // Departments can load lazily when needed
                        await Promise.all([
                            fetchQuestions(storedCompanyId, storedCompanyType),
                        ]);
                    }
                } catch (error) {
                    console.error("Error initializing data:", error);
                } finally {
                    setLoading(false);
                }
            };

            initData();
        }
    }, [signature, fetchCompanies, fetchQuestions,]);

    // Get current CommonSelect value
    const getCurrentCompanyValue = () => {
        if (!selectedCompany.id || !selectedCompany.type) {
            return companyOptions[0] || null;
        }

        const currentValue = `${selectedCompany.id}-${selectedCompany.type}`;
        return companyOptions.find(option => option.value === currentValue) || companyOptions[0];
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1" style={{ fontWeight: 600 }}>
                            <DashboardOutlined style={{ marginRight: 10 }} />
                            Mood Meter Dashboard
                        </h2>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {selectedCompany.name} • {selectedCompany.type}
                        </Text>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        <div
                            className="dropdown me-2 mb-2"
                            title="Choose Company"
                            style={{ minWidth: "200px" }}
                        >
                            <CommonSelect
                                className="select"
                                options={companyOptions}
                                defaultValue={getCurrentCompanyValue()}
                                onChange={handleCompanyChange}
                                placeHolder="Select Company"
                            />
                        </div>
                    </div>
                </div>


                {/* Main Content Tabs */}
                <Card
                    size="small"
                    className="shadow-sm"
                    bodyStyle={{ padding: '16px' }}
                >
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        size="small"
                        style={{ marginBottom: 16 }}
                    >

                        {/* Analytics Tab */}
                        <TabPane
                            tab={
                                <Space size={4}>
                                    <BarChartOutlined style={{ fontSize: 12 }} />
                                    <span>Analytics</span>
                                    <Badge
                                        count="+"
                                        size="small"
                                        style={{
                                            marginLeft: 4,
                                            fontSize: 10,
                                            backgroundColor: '#1890ff'
                                        }}
                                    />
                                </Space>
                            }
                            key="analytics"
                        >
                            <MoodAnalytics
                                selectedCompany={selectedCompany}
                                dateRange={selectedDateRange}
                                onDateRangeChange={handleDateRangeChange}
                                departments={departments.filter(dept => dept.id !== "all")}
                                questions={questions}
                                disabled={!selectedCompany.id}
                            />
                        </TabPane>
                    </Tabs>
                </Card>
            </div>

            {/* Footer */}
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <p className="mb-0">2025 © CMS.</p>
                <p>
                    Designed & Developed By{" "}
                    <Link to="#" className="text-primary">
                        ZYVO
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default MoodMeterDashboard;